import { Vector3 } from 'three';
import type { Engine } from '@/core/engine';
import type { System } from '@/core/system';
import type { Time } from '@/core/time';
import { Input } from '@/input/input';
import { City } from '@/world/city';
import { PLAYER, AI } from '@/config';
import { clamp, damp, lerp, smootherstep } from '@/utils/math';
import { CameraRig } from './camera-rig';
import type { PlayerTelemetry } from '@/core/types';

/**
 * A grounded first-person controller with source-style acceleration and simple,
 * robust city collision.
 *
 * Collision philosophy (a hard-won rule carried over from earlier projects): the
 * horizontal resolver works purely in XZ. Buildings act as walls only while the
 * player's feet are below their roof; once you are level with or above a roof it
 * becomes a walkable surface, never a wall. Vertical support is resolved
 * separately against the highest surface at-or-below the feet. This cleanly gives
 * rooftop traversal, skybridge walking and parkour without a physics engine.
 */
const _move = new Vector3();
const _telemetryPos = new Vector3();

export class PlayerController implements System {
  readonly name = 'player';

  readonly position = new Vector3(0, 0, 0); // feet on the ground plane
  private velocity = new Vector3();
  yaw = 0;
  private pitch = 0;
  private grounded = true;
  private smoothSpeed = 0;
  private coyoteTimer = 999;
  private jumpBufferTimer = 0;

  /* -------- finale lift ("The Remembering") --------
   * While the ceremony holds the player aloft, gravity and jumping are suspended
   * and the vertical position is eased (smootherstep) from where the lift began
   * toward a target height. Horizontal drift is hard-damped and clamped to a small
   * disc so the player can still look freely but can't walk off the light. Mouse
   * look and telemetry are untouched — the rest of the pipeline never notices. */
  private lifting = false;
  private liftStartY = 0;
  private liftTargetY = 0;
  private liftDuration = 0;
  private liftElapsed = 0;
  private liftOriginX = 0;
  private liftOriginZ = 0;

  private rig: CameraRig;

  constructor(
    private engine: Engine,
    private input: Input,
    private city: City,
  ) {
    this.rig = new CameraRig(engine.camera);
    // Spawn just off the exact centre, facing into the city.
    this.position.set(0, 0, 0);
    this.yaw = Math.PI * 0.25;
  }

  update(time: Time): void {
    const dt = time.delta;
    this.applyLook();
    this.applyMovement(dt);
    this.emitTelemetry(dt);
  }

  lateUpdate(time: Time): void {
    // Position the camera at eye height with subtle bob/landing motion.
    const moving = this.smoothSpeed > 0.5 && this.grounded;
    this.rig.update(
      this.position,
      this.yaw,
      this.pitch,
      this.smoothSpeed,
      moving,
      this.grounded,
      time,
    );
  }

  private applyLook(): void {
    const { dx, dy } = this.input.consumeLook();
    this.yaw -= dx * PLAYER.lookSpeed;
    this.pitch -= dy * PLAYER.lookSpeed;
    this.pitch = clamp(this.pitch, -PLAYER.pitchLimit, PLAYER.pitchLimit);
  }

  /**
   * Begin (or re-target) the finale lift: ease the player's height to `targetY`
   * over `seconds`. Safe to call again mid-lift — e.g. to descend — in which case
   * the ease re-anchors from the current height while the clamp origin is kept.
   */
  liftTo(targetY: number, seconds: number): void {
    if (!this.lifting) {
      // Anchor the clamp disc where the lift first took hold.
      this.liftOriginX = this.position.x;
      this.liftOriginZ = this.position.z;
    }
    this.lifting = true;
    this.liftStartY = this.position.y;
    this.liftTargetY = targetY;
    this.liftDuration = Math.max(1e-4, seconds);
    this.liftElapsed = 0;
    this.velocity.y = 0;
  }

  /** End the lift and restore normal physics; the ground catches the player next frame. */
  releaseLift(): void {
    if (!this.lifting) return;
    this.lifting = false;
    this.velocity.set(0, 0, 0);
    this.grounded = false;
  }

  private updateLift(dt: number): void {
    // Bleed off residual horizontal velocity so the player hangs near-still.
    const f = Math.max(0, 1 - 24 * dt);
    this.velocity.x *= f;
    this.velocity.z *= f;
    this.velocity.y = 0;

    // Integrate what's left of the drift, then clamp within 2.5m of the origin.
    let nx = this.position.x + this.velocity.x * dt;
    let nz = this.position.z + this.velocity.z * dt;
    const dx = nx - this.liftOriginX;
    const dz = nz - this.liftOriginZ;
    const d = Math.hypot(dx, dz);
    const maxR = 2.5;
    if (d > maxR) {
      const s = maxR / d;
      nx = this.liftOriginX + dx * s;
      nz = this.liftOriginZ + dz * s;
      this.velocity.x = 0;
      this.velocity.z = 0;
    }
    this.position.x = nx;
    this.position.z = nz;

    // Ease the height with smootherstep on local progress (dt-driven, not wall-clock).
    this.liftElapsed = Math.min(this.liftDuration, this.liftElapsed + dt);
    const t = this.liftElapsed / this.liftDuration;
    this.position.y = lerp(this.liftStartY, this.liftTargetY, smootherstep(t));

    this.grounded = false;
    this.smoothSpeed = damp(this.smoothSpeed, 0, 12, dt);
  }

  private applyMovement(dt: number): void {
    if (this.lifting) {
      // Gravity and jump consumption are both suspended for the duration.
      this.updateLift(dt);
      return;
    }
    const axis = this.input.moveAxis();
    // Convert body-relative intent into world XZ using yaw only (walking, not flying).
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const forwardX = -sin;
    const forwardZ = -cos;
    const rightX = cos;
    const rightZ = -sin;
    _move.set(
      forwardX * -axis.z + rightX * axis.x,
      0,
      forwardZ * -axis.z + rightZ * axis.x,
    );
    const wish = _move.length();
    if (wish > 0) _move.multiplyScalar(1 / wish);

    const maxSpeed = this.input.sprinting ? PLAYER.sprintSpeed : PLAYER.walkSpeed;
    const accel = this.grounded ? PLAYER.accel : PLAYER.airAccel;

    // Accelerate horizontal velocity toward the wish direction.
    const targetVX = _move.x * maxSpeed * wish;
    const targetVZ = _move.z * maxSpeed * wish;
    this.velocity.x += (targetVX - this.velocity.x) * Math.min(1, accel * dt * 0.1);
    this.velocity.z += (targetVZ - this.velocity.z) * Math.min(1, accel * dt * 0.1);

    // Ground friction when no input.
    if (wish === 0 && this.grounded) {
      const f = Math.max(0, 1 - PLAYER.friction * dt);
      this.velocity.x *= f;
      this.velocity.z *= f;
    }

    // Jump — with coyote time (can still jump just after walking off an edge) and
    // input buffering (a press just before landing fires the instant you touch
    // down). Both make climbing onto roofs and steps forgiving instead of fiddly.
    if (this.input.consumeJump()) this.jumpBufferTimer = PLAYER.jumpBuffer;
    else this.jumpBufferTimer = Math.max(0, this.jumpBufferTimer - dt);
    this.coyoteTimer = this.grounded ? 0 : this.coyoteTimer + dt;
    if (this.jumpBufferTimer > 0 && this.coyoteTimer <= PLAYER.coyoteTime) {
      this.velocity.y = PLAYER.jumpVelocity;
      this.grounded = false;
      this.coyoteTimer = 999; // consume the window so there's no double jump
      this.jumpBufferTimer = 0;
      this.justJumped = true;
    }

    // Gravity.
    this.velocity.y -= PLAYER.gravity * dt;

    // --- Horizontal integration + wall resolution ---
    const nextX = this.position.x + this.velocity.x * dt;
    const nextZ = this.position.z + this.velocity.z * dt;
    const resolved = this.resolveHorizontal(nextX, nextZ, this.position.y);
    if (resolved.blockedX) this.velocity.x = 0;
    if (resolved.blockedZ) this.velocity.z = 0;
    this.position.x = resolved.x;
    this.position.z = resolved.z;

    // --- Vertical integration + support ---
    const support = this.supportHeightAt(this.position.x, this.position.z, this.position.y);
    let nextY = this.position.y + this.velocity.y * dt;
    if (nextY <= support + 1e-3) {
      nextY = support;
      if (this.velocity.y < 0) this.velocity.y = 0;
      this.grounded = true;
    } else {
      this.grounded = false;
    }
    this.position.y = nextY;

    const horizSpeed = Math.hypot(this.velocity.x, this.velocity.z);
    this.smoothSpeed = damp(this.smoothSpeed, horizSpeed, 12, dt);
  }

  /* -------- collision queries against the city -------- */

  private forEachNearbyBuilding(
    x: number,
    z: number,
    fn: (top: number, minX: number, maxX: number, minZ: number, maxZ: number) => void,
  ): void {
    const { cx, cz } = this.city.worldToCell(x, z);
    for (const b of this.city.buildings) {
      if (b.dying || b.slot < 0) continue;
      if (Math.abs(b.cx - cx) > 1 || Math.abs(b.cz - cz) > 1) continue;
      const top = b.height * Math.min(1, Math.max(0, b.growth));
      const hw = b.width * 0.5;
      const hd = b.depth * 0.5;
      fn(top, b.x - hw, b.x + hw, b.z - hd, b.z + hd);
    }
  }

  private resolveHorizontal(
    x: number,
    z: number,
    feetY: number,
  ): { x: number; z: number; blockedX: boolean; blockedZ: boolean } {
    const r = PLAYER.radius;
    let outX = x;
    let outZ = z;
    let blockedX = false;
    let blockedZ = false;

    this.forEachNearbyBuilding(x, z, (top, minX, maxX, minZ, maxZ) => {
      // Only a wall if its roof is above where we can step.
      if (top <= feetY + PLAYER.maxStepHeight) return;
      const eMinX = minX - r;
      const eMaxX = maxX + r;
      const eMinZ = minZ - r;
      const eMaxZ = maxZ + r;
      if (outX < eMinX || outX > eMaxX || outZ < eMinZ || outZ > eMaxZ) return;
      // Push out along the axis of least penetration.
      const penL = outX - eMinX;
      const penR = eMaxX - outX;
      const penB = outZ - eMinZ;
      const penF = eMaxZ - outZ;
      const minPen = Math.min(penL, penR, penB, penF);
      if (minPen === penL) {
        outX = eMinX;
        blockedX = true;
      } else if (minPen === penR) {
        outX = eMaxX;
        blockedX = true;
      } else if (minPen === penB) {
        outZ = eMinZ;
        blockedZ = true;
      } else {
        outZ = eMaxZ;
        blockedZ = true;
      }
    });

    return { x: outX, z: outZ, blockedX, blockedZ };
  }

  private supportHeightAt(x: number, z: number, feetY: number): number {
    let support = 0; // the ground plane
    const step = PLAYER.maxStepHeight;

    this.forEachNearbyBuilding(x, z, (top, minX, maxX, minZ, maxZ) => {
      if (x < minX || x > maxX || z < minZ || z > maxZ) return;
      if (top <= feetY + step && top > support) support = top;
    });

    // Skybridges & platforms are also surfaces.
    for (const link of this.city.links) {
      if (link.slot < 0 || link.growth < 0.5) continue;
      const top = this.linkTopAt(link, x, z);
      if (top !== null && top <= feetY + step && top > support) support = top;
    }

    return support;
  }

  private linkTopAt(
    link: import('@/world/city').SkyLink,
    x: number,
    z: number,
  ): number | null {
    if (link.kind === 'platform') {
      const half = (link.width * link.growth) * 0.5;
      if (Math.abs(x - link.ax) <= half && Math.abs(z - link.az) <= half) {
        return link.ay + 0.35;
      }
      return null;
    }
    // Bridge: distance from point to the segment A->B within its width.
    const ax = link.ax;
    const az = link.az;
    const bx = link.bx;
    const bz = link.bz;
    const dx = bx - ax;
    const dz = bz - az;
    const len2 = dx * dx + dz * dz;
    if (len2 < 1e-4) return null;
    let t = ((x - ax) * dx + (z - az) * dz) / len2;
    t = clamp(t, 0, link.growth);
    const px = ax + dx * t;
    const pz = az + dz * t;
    const dist = Math.hypot(x - px, z - pz);
    if (dist > link.width * 0.5) return null;
    const y = link.ay + (link.by - link.ay) * t;
    return y + 0.3;
  }

  /* -------- telemetry for the analyzer -------- */

  private enclosureAt(x: number, z: number): number {
    // Openness = distance to the nearest surrounding building wall, normalized.
    let nearest = Infinity;
    this.forEachNearbyBuilding(x, z, (top, minX, maxX, minZ, maxZ) => {
      if (top < 2) return;
      const dx = Math.max(minX - x, 0, x - maxX);
      const dz = Math.max(minZ - z, 0, z - maxZ);
      const d = Math.hypot(dx, dz);
      if (d < nearest) nearest = d;
    });
    if (!isFinite(nearest)) return 1;
    return clamp(nearest / (this.city.cellSize * 0.9), 0, 1);
  }

  private emitTelemetry(dt: number): void {
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    _telemetryPos.copy(this.position);
    const telemetry: PlayerTelemetry = {
      position: _telemetryPos,
      speed,
      height: this.position.y,
      elevated: this.position.y > 0.75,
      enclosure: this.enclosureAt(this.position.x, this.position.z),
      lookPitch: this.pitch,
      still: speed < AI.stillSpeed,
      dt,
      jumped: this.justJumped,
    };
    this.justJumped = false;
    this.engine.events.emit('player:telemetry', telemetry);
  }

  private justJumped = false;

  dispose(): void {
    // Camera rig holds no disposable GPU resources.
  }
}
